import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appPath } from '@/api/client';
import { useRecordPost } from '@/api/RecordContext';
import { failureOf, said } from '@/api/errors';
import { useAssignees } from '@/api/hooks';
import { useMeOrThrow } from '@/app/MeContext';
import { Select, Textarea } from '@/ui/fields';
import { Badge, Button, Field, Kicker, TextInput } from '@/ui/primitives';
import { useToast } from '@/ui/Toast';

/**
 * What the Norum Sync asked to follow through (proposal 0012): other kinds
 * of buyer probed, a few of each, and what came of each kind (E7); the
 * first email read by a native reader before it is approved (E8); what
 * threatens a harvest where the product grows (E13); and what came of a
 * follow-up call (E16). Each is a person's to write; the platform counts.
 */

const P = {
  probeCap: appPath('/v1/plans/probe-cap'),
  probes: appPath('/v1/plans/probes'),
  reviews: appPath('/v1/outreach/sequences/reviews'),
  askReview: appPath('/v1/outreach/sequences/review/ask'),
  answerReview: appPath('/v1/outreach/sequences/review/answer'),
  risks: appPath('/v1/signals/supply-risks'),
  addRisk: appPath('/v1/signals/supply-risk/add'),
  callOutcome: appPath('/v1/cadences/call-outcome'),
};

// ---------------------------------------------------------------------------
// E7: the kinds a plan looks for, side by side
// ---------------------------------------------------------------------------

interface ProbeKind { profileId: string; segment: string; main: boolean; cap: number | null; found: number; aboveBar: number; drafted: number; replied: number; reached: number; lost: number }

export function ProbeKinds({ planId, decides }: { planId: string; decides: boolean }) {
  const post = useRecordPost();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery<{ kinds: ProbeKind[] }>({ queryKey: ['plan-probes', planId], queryFn: () => post(P.probes, { planId }) });
  const cap = useMutation({
    mutationFn: (input: { profileId: string; cap: number | null }) => post(P.probeCap, { planId, ...input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['plan-probes', planId] }),
    onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }),
  });
  const [caps, setCaps] = useState<Record<string, string>>({});
  const kinds = q.data?.kinds ?? [];
  // One kind is the plan itself; there is nothing to compare it with.
  if (kinds.length < 2) return null;
  return (
    <section className="flex flex-col gap-2" data-testid="plan-probes">
      <Kicker>Each kind it looks for</Kicker>
      <p className="m-0 text-dense text-secondary">
        A probe is another kind of buyer looked for beside the plan’s own, a few of it at most, to see whether it answers. Once it has found its cap, it is looked for no
        further.
      </p>
      <div className="overflow-x-auto border border-line">
        <table className="w-full border-collapse text-dense">
          <thead>
            <tr>
              {['Kind', 'Found', 'Above the bar', 'Written to', 'Replied', 'Reached by phone', 'Lost', 'At most'].map((h) => (
                <th key={h} className="border-b border-line p-2 text-left text-meta">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {kinds.map((k) => (
              <tr key={k.profileId}>
                <td className="border-b border-line p-2">
                  {k.segment} {k.main && <Badge plain tone="accent">The plan’s own</Badge>}
                </td>
                {[k.found, k.aboveBar, k.drafted, k.replied, k.reached, k.lost].map((n, i) => (
                  <td key={i} className="border-b border-line p-2 font-mono">
                    {n}
                  </td>
                ))}
                <td className="border-b border-line p-2">
                  {k.main ? (
                    <span className="text-meta">No cap</span>
                  ) : decides ? (
                    <span className="flex items-center gap-1">
                      <TextInput
                        aria-label={`At most, of ${k.segment}`}
                        className="w-20"
                        value={caps[k.profileId] ?? (k.cap === null ? '' : String(k.cap))}
                        placeholder="none"
                        onChange={(e) => setCaps({ ...caps, [k.profileId]: e.target.value })}
                      />
                      <Button
                        size="row"
                        disabled={cap.isPending}
                        onClick={() => {
                          const raw = (caps[k.profileId] ?? '').trim();
                          cap.mutate({ profileId: k.profileId, cap: raw === '' ? null : Number(raw) }, { onSuccess: () => toast({ text: raw ? `It looks for at most ${raw} of ${k.segment}.` : `No cap on ${k.segment}.` }) });
                        }}
                      >
                        Set
                      </Button>
                    </span>
                  ) : (
                    <span>{k.cap ?? 'No cap'}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// E8: a template's readers
// ---------------------------------------------------------------------------

interface Review { id: string; reviewerId: string; reviewer: string; askedBy: string; askedAt: string; verdict: 'reads_well' | 'needs_changes' | null; note: string | null; answeredAt: string | null }

export function SequenceReaders({ sequenceId, name, draft }: { sequenceId: string; name: string; draft: boolean }) {
  const post = useRecordPost();
  const me = useMeOrThrow();
  const toast = useToast();
  const qc = useQueryClient();
  const assignees = useAssignees(me.department);
  const q = useQuery<{ reviews: Review[] }>({ queryKey: ['sequence-reviews', sequenceId], queryFn: () => post(P.reviews, { sequenceId }) });
  const done = () => {
    void qc.invalidateQueries({ queryKey: ['sequence-reviews', sequenceId] });
    void qc.invalidateQueries({ queryKey: ['sequences'] });
  };
  const failed = (e: unknown) => toast({ text: said(failureOf(e)), tone: 'danger' });
  const ask = useMutation({ mutationFn: (reviewerId: string) => post(P.askReview, { sequenceId, reviewerId }), onSuccess: done, onError: failed });
  const answer = useMutation({ mutationFn: (input: { reviewId: string; verdict: 'reads_well' | 'needs_changes'; note: string }) => post(P.answerReview, input), onSuccess: done, onError: failed });
  const [who, setWho] = useState('');
  const [note, setNote] = useState('');
  const reviews = q.data?.reviews ?? [];
  const people = (assignees.data?.people ?? []).filter((p) => p.id !== me.humanId);
  return (
    <div className="flex flex-col gap-2 border-b border-line py-2" data-testid="sequence-readers">
      <span className="text-dense font-semibold">{name}</span>
      {reviews.length === 0 && <span className="text-dense text-meta">Nobody has been asked to read it.</span>}
      {reviews.map((r) => (
        <div key={r.id} className="flex flex-col gap-1 text-dense">
          <span className="flex flex-wrap items-center gap-2">
            {r.reviewer}, asked by {r.askedBy}
            <Badge plain tone={r.verdict === 'reads_well' ? 'accent' : r.verdict === 'needs_changes' ? 'danger' : 'warning'}>
              {r.verdict === 'reads_well' ? 'Reads well' : r.verdict === 'needs_changes' ? 'Needs changes' : 'Not read yet'}
            </Badge>
          </span>
          {r.note && <span className="text-secondary">{r.note}</span>}
          {r.verdict === null && r.reviewerId === me.humanId && (
            <div className="flex flex-wrap items-end gap-2">
              <Field label="What you would change">
                <Textarea aria-label={`What you would change in ${name}`} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
              </Field>
              <Button size="row" disabled={answer.isPending} onClick={() => answer.mutate({ reviewId: r.id, verdict: 'reads_well', note }, { onSuccess: () => toast({ text: 'Said. It can be approved now.' }) })}>
                It reads well
              </Button>
              <Button size="row" disabled={answer.isPending || note.trim().length < 3} onClick={() => answer.mutate({ reviewId: r.id, verdict: 'needs_changes', note }, { onSuccess: () => toast({ text: 'Said. Its author writes it again.' }) })}>
                It needs changes
              </Button>
            </div>
          )}
        </div>
      ))}
      {draft && (
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Ask someone who reads the language">
            <Select aria-label={`Who reads ${name}`} value={who} onChange={(e) => setWho(e.target.value)} options={[{ value: '', label: 'Choose a colleague' }, ...people.map((p) => ({ value: p.id, label: p.displayName }))]} />
          </Field>
          <Button size="row" disabled={!who || ask.isPending} onClick={() => ask.mutate(who, { onSuccess: () => toast({ text: 'Asked. It is approved once they have read it.' }) })}>
            Ask them to read it
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// E13: what threatens a harvest
// ---------------------------------------------------------------------------

interface Risk { id: string; origin: string; said: string; url: string; source: string | null; sourceId: string | null; seenOn: string }

export function SupplyRisks() {
  const post = useRecordPost();
  const me = useMeOrThrow();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery<{ risks: Risk[] }>({ queryKey: ['supply-risks', me.department], queryFn: () => post(P.risks, {}) });
  const add = useMutation({
    mutationFn: (input: { country: string; said: string; url: string }) => post<{ existing: boolean }>(P.addRisk, input),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['supply-risks'] });
      toast({ text: r.existing ? 'That page is on record already.' : 'Written down, with its page.' });
      setSaid('');
      setUrl('');
    },
    onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }),
  });
  const [origin, setOrigin] = useState('ES');
  const [what, setSaid] = useState('');
  const [url, setUrl] = useState('');
  const risks = q.data?.risks ?? [];
  const origins = [...new Set(risks.map((r) => r.origin))];
  return (
    <section className="flex flex-col gap-3" data-testid="supply-risks">
      <Kicker>Risk where it grows</Kicker>
      <p className="m-0 text-dense text-secondary">
        A heatwave, a drought or a poor harvest in the country the product comes from: a reason to ask a supplier what it means for what they can offer. Read from a
        source the ledger names, or written down here with the page it is on.
      </p>
      {risks.length === 0 && <p className="m-0 text-dense text-meta">Nothing on record this year.</p>}
      {origins.map((o) => (
        <div key={o} className="flex flex-col gap-1">
          <span className="text-dense font-semibold">{o}</span>
          {risks
            .filter((r) => r.origin === o)
            .map((r) => (
              <span key={r.id} className="text-dense">
                {r.seenOn}: {r.said}{' '}
                <a href={r.url} target="_blank" rel="noreferrer noopener" className="text-accent underline">
                  the page
                </a>
                {r.source ? <span className="text-meta"> · {r.source}</span> : null}
              </span>
            ))}
        </div>
      ))}
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Where it grows">
          <TextInput aria-label="Origin country" className="w-20" maxLength={2} value={origin} onChange={(e) => setOrigin(e.target.value.toUpperCase())} />
        </Field>
        <Field label="What is at risk">
          <TextInput aria-label="What is at risk" value={what} onChange={(e) => setSaid(e.target.value)} placeholder="The summer’s heat cut the garlic harvest in Castilla-La Mancha" />
        </Field>
        <Field label="The page">
          <TextInput aria-label="The page it is on" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://" />
        </Field>
        <Button size="row" disabled={add.isPending || !what.trim() || !url.trim()} onClick={() => add.mutate({ country: origin, said: what, url })}>
          Write it down
        </Button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// E16: what came of a call
// ---------------------------------------------------------------------------

const OUTCOMES = [
  { value: 'reached', label: 'We spoke' },
  { value: 'not_interested', label: 'We spoke; they said no' },
  { value: 'call_back', label: 'Call them back on a day' },
  { value: 'voicemail', label: 'Left a message' },
  { value: 'no_answer', label: 'Nobody answered' },
  { value: 'wrong_number', label: 'Wrong number' },
] as const;

export const CALL_WORDS: Record<(typeof OUTCOMES)[number]['value'], string> = Object.fromEntries(OUTCOMES.map((o) => [o.value, o.label])) as never;

export function CallOutcome({ taskId, step }: { taskId: string; step: { id: string; outcome: (typeof OUTCOMES)[number]['value'] | null; callBackOn: string | null } }) {
  const post = useRecordPost();
  const toast = useToast();
  const qc = useQueryClient();
  const [outcome, setOutcome] = useState<string>('');
  const [day, setDay] = useState('');
  const [note, setNote] = useState('');
  const record = useMutation({
    mutationFn: () => post<{ stopped: 'replied' | 'lost' | null }>(P.callOutcome, { stepId: step.id, outcome, note, ...(outcome === 'call_back' ? { callBackOn: day } : {}) }),
    onSuccess: (r) => {
      for (const key of ['task', 'tasks', 'today']) void qc.invalidateQueries({ queryKey: [key] });
      toast({ text: r.stopped === 'replied' ? 'Recorded. The follow-up stops: you spoke.' : r.stopped === 'lost' ? 'Recorded. The follow-up stops; mark them lost with the reason on their page.' : 'Recorded. The follow-up carries on.' });
    },
    onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }),
  });
  if (step.outcome) {
    return (
      <section className="flex flex-col gap-1" data-testid="call-outcome">
        <Kicker>What came of the call</Kicker>
        <span className="text-dense">
          {CALL_WORDS[step.outcome]}
          {step.callBackOn ? `, on ${step.callBackOn}` : ''}.
        </span>
      </section>
    );
  }
  return (
    <section className="flex flex-col gap-2" data-testid="call-outcome" aria-label={`What came of the call, task ${taskId}`}>
      <Kicker>What came of the call</Kicker>
      <div className="flex flex-wrap items-end gap-2">
        <Field label="It went">
          <Select aria-label="What came of the call" value={outcome} onChange={(e) => setOutcome(e.target.value)} options={[{ value: '', label: 'Choose' }, ...OUTCOMES.map((o) => ({ value: o.value, label: o.label }))]} />
        </Field>
        {outcome === 'call_back' && (
          <Field label="On">
            <TextInput type="date" aria-label="Call back on" value={day} onChange={(e) => setDay(e.target.value)} />
          </Field>
        )}
        <Field label="What was said">
          <Textarea aria-label="What was said on the call" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <Button size="row" disabled={!outcome || record.isPending || (outcome === 'call_back' && !day)} onClick={() => record.mutate()}>
          Record it
        </Button>
      </div>
    </section>
  );
}
